import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Box,
  Spinner,
  Text,
  Cascader,
  CascaderComponents,
  type CascaderOption,
  type CascaderComponentProps,
  CascaderPositioningOptions,
} from '@frontend/components';

import { useAppSelector } from 'store/hooks';
import { initMaterialsStore } from 'store/materials/actions';
import {
  getStoredMaterialOptionsForCascader,
  getMaterialClassByMaterialId,
  getMaterialFamilyByMaterialId,
} from 'store/materials/reducer';

import { adaptMaterialOptionsToCascaderSubmenu } from './adaptMaterialOptionsToCascader';
import type {
  MaterialSelectorChangeHandler,
  MaterialCascaderNode,
} from './types';

export type MaterialCascaderValuePath = (string | number)[];

const LastLabelSingleSelectedValue = ({
  selectedOption,
  getCommonProps: _,
  ...rest
}: CascaderComponentProps<CascaderOption>['SingleSelectedValue']) => {
  const last =
    selectedOption.fullLabels[selectedOption.fullLabels.length - 1] ?? '';
  return (
    <Box {...rest}>
      <Text size="md" as="span">
        {last}
      </Text>
    </Box>
  );
};

const MaterialCascaderInput = (
  props: CascaderComponentProps<CascaderOption>['Input'],
) => <CascaderComponents.Input {...props} aria-label="Select Material" />;

const MaterialCascaderControl = ({
  children,
  getCommonProps,
  ...rest
}: CascaderComponentProps<CascaderOption>['Control']) => {
  const busy = getCommonProps().getIsLoading();
  return (
    <Box position="relative" width="100%">
      <CascaderComponents.Control getCommonProps={getCommonProps} {...rest}>
        {children}
      </CascaderComponents.Control>
      {busy ? (
        <Box
          alignItems="center"
          aria-busy
          borderRadius="md"
          display="flex"
          inset={0}
          justifyContent="center"
          pointerEvents="auto"
          position="absolute"
        >
          <Spinner color="feature.secondary" size="sm" />
        </Box>
      ) : null}
    </Box>
  );
};

type MaterialSelectorProps = {
  cascaderOptions?: MaterialCascaderNode[];
  canSelectSubmenu?: boolean;
  disabled: boolean;
  onChange: MaterialSelectorChangeHandler;
  placeholder: string;
  materialId: number | null | string;
  shouldSingleSelect?: boolean;
  supplierMaterialId: number | null | string;
  valueArray?: MaterialCascaderValuePath | null;
  searchModePositioning?: CascaderPositioningOptions;
  menuModePositioning?: CascaderPositioningOptions;
};

const toPathStrings = (
  path: MaterialCascaderValuePath | null | undefined,
): string[] => (path ?? []).map(String);

const segmentForParent = (path: string[], index: number): number | string => {
  const s = path[index];
  if (s === undefined || s === '') {
    return '';
  }
  const n = Number(s);
  return Number.isNaN(n) ? s : n;
};

const emptySegmentToNull = (v: number | string): number | string | null =>
  v === '' ? null : v;

const parseMaterialPath = (path: string[]) => ({
  classId: segmentForParent(path, 0),
  familyId: segmentForParent(path, 1),
  materialId: segmentForParent(path, 2),
  supplierMaterialId: emptySegmentToNull(segmentForParent(path, 3)),
});

const materialHasSupplierTierInOptions = (
  classes: MaterialCascaderNode[],
  mid: number | string | null | undefined,
): boolean => {
  if (mid == null || mid === '') {
    return false;
  }
  const id = String(mid);
  for (const matClass of classes) {
    for (const family of matClass.children ?? []) {
      for (const material of family.children ?? []) {
        if (String(material.value) === id) {
          return (
            Array.isArray(material.children) && material.children.length > 0
          );
        }
      }
    }
  }
  return false;
};

const getMaterialSingleValuePath = (
  materialOptions: MaterialCascaderNode[],
  classId: number | string,
  familyId: number | string,
  mid: number | string | null | undefined,
  supplierMaterialId: number | string | null | undefined,
): MaterialCascaderValuePath | null => {
  if (mid == null || mid === '') {
    return null;
  }
  const useSupplier =
    Boolean(supplierMaterialId && mid) &&
    materialHasSupplierTierInOptions(materialOptions, mid);
  if (useSupplier) {
    return [classId, familyId, mid, supplierMaterialId as number | string];
  }
  return [classId, familyId, mid];
};

const pathToKey = (path: MaterialCascaderValuePath | null) =>
  path?.length ? path.map(String).join('/') : '';

const MaterialSelector = ({
  cascaderOptions,
  canSelectSubmenu = false,
  disabled,
  onChange,
  placeholder,
  materialId,
  shouldSingleSelect = true,
  supplierMaterialId,
  valueArray,
  // NB: This is bad prop design, they should be spread, but right now, the props are too coupled one level up and it's risky to pass them any other way. Sorry.
  searchModePositioning,
  menuModePositioning,
}: MaterialSelectorProps) => {
  const dispatch = useDispatch();
  const initMaterialStore = useCallback(() => {
    dispatch(initMaterialsStore());
  }, [dispatch]);

  const materialOptions = useAppSelector(
    (state) => cascaderOptions || getStoredMaterialOptionsForCascader(state),
  );
  const materialClass = useAppSelector((state) =>
    getMaterialClassByMaterialId(state, materialId),
  );
  const materialFamily = useAppSelector((state) =>
    getMaterialFamilyByMaterialId(state, materialId),
  );

  const [hasInitiated, setHasInitiated] = useState(false);
  const [pendingPathKey, setPendingPathKey] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (materialOptions.length == 0 && !hasInitiated) {
      initMaterialStore();
      setHasInitiated(true);
    }
  }, [hasInitiated, initMaterialStore, materialOptions.length]);

  const cascaderSubmenu = useMemo(
    () => adaptMaterialOptionsToCascaderSubmenu(materialOptions),
    [materialOptions],
  );

  const components = useMemo(
    () => ({
      Control: MaterialCascaderControl,
      Input: MaterialCascaderInput,
      ...(shouldSingleSelect
        ? { SingleSelectedValue: LastLabelSingleSelectedValue }
        : {}),
    }),
    [shouldSingleSelect],
  );

  const materialClassId = (materialClass || {}).id ?? '';
  const materialFamilyId = (materialFamily || {}).id ?? '';
  const valuePath = useMemo(
    () =>
      shouldSingleSelect
        ? getMaterialSingleValuePath(
            materialOptions,
            materialClassId,
            materialFamilyId,
            materialId,
            supplierMaterialId,
          )
        : valueArray ?? null,
    [
      materialOptions,
      materialClassId,
      materialFamilyId,
      materialId,
      supplierMaterialId,
      shouldSingleSelect,
      valueArray,
    ],
  );

  const valueStrings = valuePath ? toPathStrings(valuePath) : [];
  const cascaderValue = valueStrings;
  const resolvedPathKey = pathToKey(valuePath);

  useEffect(() => {
    if (pendingPathKey === undefined) {
      return;
    }
    if (pendingPathKey === resolvedPathKey) {
      setPendingPathKey(undefined);
    }
  }, [pendingPathKey, resolvedPathKey]);

  const isLoadingSelection =
    shouldSingleSelect &&
    pendingPathKey !== undefined &&
    pendingPathKey !== resolvedPathKey;

  const handleLibraryChange = useCallback(
    (selectedPath: string[]) => {
      if (!selectedPath.length) {
        if (shouldSingleSelect) {
          setPendingPathKey('');
        }
        onChange({
          classId: null,
          familyId: null,
          materialId: null,
          supplierMaterialId: null,
        });
        return;
      }

      const { classId, familyId, materialId, supplierMaterialId } =
        parseMaterialPath(selectedPath);

      if (shouldSingleSelect) {
        const hasSupplierTier = materialHasSupplierTierInOptions(
          materialOptions,
          materialId,
        );
        const isMaterialFullySpecified =
          materialId !== '' && (!hasSupplierTier || supplierMaterialId != null);

        if (isMaterialFullySpecified) {
          const nextPath = getMaterialSingleValuePath(
            materialOptions,
            classId,
            familyId,
            materialId,
            supplierMaterialId,
          );
          setPendingPathKey(pathToKey(nextPath));
        }
      }

      onChange({ classId, familyId, materialId, supplierMaterialId });
    },
    [materialOptions, shouldSingleSelect, onChange],
  );

  return (
    <Box position="relative" width="100%">
      <Cascader<CascaderOption>
        canSelectSubmenu={canSelectSubmenu}
        className="material-selector-cascader"
        components={components}
        isClearable
        isDisabled={disabled}
        isLoading={!disabled && isLoadingSelection}
        onChange={handleLibraryChange}
        options={cascaderSubmenu}
        placeholder={placeholder}
        value={cascaderValue}
        searchModePositioning={searchModePositioning}
        menuModePositioning={menuModePositioning}
      />
    </Box>
  );
};

export default MaterialSelector;
